// Weekly CEFR-anchored output prompts. One per sub-level, mix of writing/speaking.
// Each prompt lists self-assessment criteria the learner ticks after submission.

export type TacheMode = 'writing' | 'speaking';

export interface TachePrompt {
  id: string;
  cefr: string;
  mode: TacheMode;
  title: string;
  scenario: string;
  /** Suggested length in words (writing) or minutes (speaking). */
  target: string;
  /** Self-assessment criteria — checkbox list. */
  criteria: string[];
  /** Lexical/grammatical pointers (in French). */
  hints: string[];
}

export const TACHE_PROMPTS: TachePrompt[] = [
  { id: 't_a11_w', cefr: 'A1.1', mode: 'writing', title: 'Carte postale',
    scenario: 'Écrivez une carte postale à un ami français pour lui dire que vous êtes arrivé à Stavanger.',
    target: '40–60 mots',
    criteria: [
      'J’ai dit où je suis (Jeg er i Stavanger).',
      'J’ai mentionné le temps qu’il fait.',
      'J’ai exprimé un sentiment simple (Det er fint / kaldt).',
      'J’ai signé avec une formule de salutation (Hilsen).',
    ],
    hints: ['Verbes au présent', 'Vocabulaire météo (regn, snø, sol)', 'Hilsen, Klem, Vi snakkes'] },

  { id: 't_a12_s', cefr: 'A1.2', mode: 'speaking', title: 'Présentation à voix haute',
    scenario: 'Présentez-vous à voix haute pendant une minute : nom, origine, profession, où vous habitez.',
    target: '1 minute',
    criteria: [
      'J’ai dit mon nom et mon âge.',
      'J’ai dit d’où je viens.',
      'J’ai dit ce que je fais dans la vie.',
      'J’ai dit depuis combien de temps je suis en Norvège.',
    ],
    hints: ['Jeg heter / Jeg er / Jeg bor i / Jeg jobber som', 'Numéros', 'Jeg har vært i Norge i …'] },

  { id: 't_a13_w', cefr: 'A1.3', mode: 'writing', title: 'Mot au propriétaire',
    scenario: 'Écrivez un courriel court à votre propriétaire pour signaler un problème avec le chauffage (varmen).',
    target: '60–80 mots',
    criteria: [
      'J’ai salué poliment (Hei / God dag).',
      'J’ai décrit le problème en une ou deux phrases.',
      'J’ai demandé si quelqu’un peut venir réparer.',
      'J’ai signé avec mon nom et mon numéro d’appartement.',
    ],
    hints: ['Varmen virker ikke', 'Kan dere komme og se på det?', 'Med vennlig hilsen'] },

  { id: 't_a14_w', cefr: 'A1.4', mode: 'writing', title: 'SMS de rendez-vous',
    scenario: 'Envoyez un SMS à un collègue pour proposer un café après le travail vendredi.',
    target: '30–50 mots',
    criteria: [
      'J’ai proposé un lieu précis.',
      'J’ai proposé une heure.',
      'J’ai demandé si ça lui convient.',
    ],
    hints: ['Skal vi ta en kaffe?', 'På fredag etter jobb?', 'Passer det for deg?'] },

  { id: 't_a21_w', cefr: 'A2.1', mode: 'writing', title: 'Récit du week-end',
    scenario: 'Racontez en quelques lignes ce que vous avez fait le week-end dernier.',
    target: '80–120 mots',
    criteria: [
      'J’ai utilisé le preteritum (verbes au passé).',
      'J’ai mentionné au moins trois activités différentes.',
      'J’ai donné un avis (Det var hyggelig / kjedelig).',
      'J’ai utilisé au moins un connecteur (først, så, etterpå).',
    ],
    hints: ['I helgen var jeg …', 'Først … så … etterpå …', 'Det var en fin / rolig helg'] },

  { id: 't_a22_s', cefr: 'A2.2', mode: 'speaking', title: 'Recette à l’oral',
    scenario: 'Expliquez à voix haute comment préparer un plat simple que vous aimez (boller, kanelsnurrer, fiskesuppe…).',
    target: '2 minutes',
    criteria: [
      'J’ai énuméré les ingrédients.',
      'J’ai utilisé des impératifs ou « du må » pour les instructions.',
      'J’ai structuré avec « først / deretter / til slutt ».',
      'J’ai donné une astuce personnelle.',
    ],
    hints: ['Du trenger … (gram, dl, ts)', 'Bland / stek / kok / hev', 'Først …, deretter …, til slutt …'] },

  { id: 't_a23_w', cefr: 'A2.3', mode: 'writing', title: 'France vs Norvège',
    scenario: 'Comparez en un paragraphe deux aspects de la vie quotidienne entre la France et la Norvège (climat, repas, transports…).',
    target: '120–150 mots',
    criteria: [
      'J’ai utilisé des comparatifs (mer / mindre / like + adj).',
      'J’ai donné au moins deux exemples concrets.',
      'J’ai exprimé mon opinion personnelle (Jeg synes …).',
      'J’ai utilisé un connecteur de contraste (men, derimot, mens).',
    ],
    hints: ['I Frankrike … mens i Norge …', 'Det er mer/mindre … enn …', 'Jeg synes / mener / tror at …'] },

  { id: 't_a24_w', cefr: 'A2.4', mode: 'writing', title: 'Réponse à une offre d’emploi',
    scenario: 'Rédigez une courte lettre de motivation pour un poste qui vous intéresse à Stavanger.',
    target: '150–180 mots',
    criteria: [
      'J’ai indiqué pour quel poste je postule.',
      'J’ai listé deux compétences pertinentes.',
      'J’ai expliqué pourquoi je veux travailler dans cette entreprise.',
      'J’ai utilisé un ton formel (de + nom, formules polies).',
    ],
    hints: ['Jeg søker stillingen som …', 'Jeg har erfaring fra / med …', 'Jeg ser fram til å høre fra dere'] },

  { id: 't_b11_s', cefr: 'B1.1', mode: 'speaking', title: 'Mini-débat à voix haute',
    scenario: 'Pour ou contre : « Le télétravail est meilleur que le travail au bureau. » Argumentez à voix haute.',
    target: '3 minutes',
    criteria: [
      'J’ai présenté clairement ma position.',
      'J’ai donné au moins trois arguments.',
      'J’ai utilisé des connecteurs logiques (for det første, dessuten, til tross for).',
      'J’ai anticipé un contre-argument et y ai répondu.',
    ],
    hints: ['Jeg mener at …', 'For det første … for det andre …', 'Det stemmer at … men …'] },

  { id: 't_b12_w', cefr: 'B1.2', mode: 'writing', title: 'Lettre à NRK',
    scenario: 'Écrivez une lettre d’opinion à NRK sur un sujet d’actualité qui vous tient à cœur (transports publics, prix du logement, climat).',
    target: '200–250 mots',
    criteria: [
      'J’ai une introduction qui pose le sujet.',
      'J’ai au moins deux paragraphes d’argument.',
      'J’ai une conclusion qui propose une action.',
      'J’ai varié les structures de phrase (subordonnées, conjugaison nuancée).',
      'J’ai utilisé un registre formel.',
    ],
    hints: ['Til redaksjonen', 'Etter min mening …', 'Det er på høy tid at …', 'Med vennlig hilsen'] },
];
